// Hand-rolled grid for the v2 (Attio-style) views.
//
// Plain <table> with sticky <thead>, sortable column headers, optional
// row selection checkbox and click handler. No virtualisation: we
// already paginate server-side via continuation tokens, so the rendered
// row count is bounded by the page size. No column resize, reorder, or
// visibility toggle in v1 — those are deliberate simplifications to
// stay zero-dep. Phase 3 adds <EditableCell> for the inline-edit cells
// without changing this grid's API.

import { useId, useState, type ReactNode } from 'react';
import type { GridColumn } from './types.js';
import type { SortKey } from './useFilters.js';

interface DataGridProps<T, C> {
  columns: GridColumn<T, C>[];
  rows: T[];
  rowKey(row: T): string;
  /** Click anywhere on a row (except cell-internal links). */
  onRowClick?(row: T): void;
  /** Current sort. Headers with a matching `sortKey` show an indicator. */
  sort?: SortKey;
  onSortChange?(key: SortKey): void;
  /** Optional row-selection checkbox column. */
  selection?: {
    selected: Set<string>;
    onChange(next: Set<string>): void;
  };
  /** Per-row context passed into each cell renderer. Shape is decided by
   *  the column set so contact grids and dial-entry grids can declare
   *  different callback surfaces. */
  context: C;
  /** Compact halves vertical padding; comfortable is the default. */
  density?: 'compact' | 'comfortable';
  loading?: boolean;
  empty?: ReactNode;
}

export function DataGrid<T, C>({
  columns,
  rows,
  rowKey,
  onRowClick,
  sort,
  onSortChange,
  selection,
  context,
  density = 'comfortable',
  loading,
  empty
}: DataGridProps<T, C>) {
  const rowPad = density === 'compact' ? 'py-1' : 'py-2';
  const headPad = density === 'compact' ? 'py-1.5' : 'py-2.5';
  const captionId = useId();
  const allSelected =
    selection !== undefined && rows.length > 0 && rows.every((r) => selection.selected.has(rowKey(r)));
  const someSelected =
    selection !== undefined && rows.some((r) => selection.selected.has(rowKey(r))) && !allSelected;

  function toggleAll() {
    if (!selection) return;
    if (allSelected) selection.onChange(new Set());
    else selection.onChange(new Set(rows.map((r) => rowKey(r))));
  }

  function toggleRow(id: string) {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selection.onChange(next);
  }

  return (
    <div className="surface border border-divider rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" aria-describedby={captionId}>
          <thead className="text-left text-xs uppercase tracking-wider text-muted surface-muted sticky top-0 z-10">
            <tr>
              {selection && (
                <th className={`w-9 px-3 ${headPad} border-b border-divider`}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
              )}
              {columns.map((col) => {
                const isSorted = sort && col.sortKey === sort;
                const sortable = Boolean(col.sortKey && onSortChange);
                const mobileHide = col.mobile === 'hide' ? 'hidden md:table-cell' : '';
                return (
                  <th
                    key={col.id}
                    className={`px-3 ${headPad} border-b border-divider font-medium align-middle ${mobileHide}`}
                    style={col.width ? { width: col.width } : undefined}
                    aria-sort={isSorted ? 'ascending' : undefined}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => col.sortKey && onSortChange?.(col.sortKey)}
                        className="inline-flex items-center gap-1 uppercase tracking-wider text-xs hover:text-strong"
                      >
                        <span>{col.label}</span>
                        {isSorted && <SortIcon />}
                      </button>
                    ) : (
                      <span>{col.label}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selection ? 1 : 0)}
                  className="px-4 py-12 text-center text-muted"
                >
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selection ? 1 : 0)}
                  className="px-4 py-12 text-center text-muted"
                >
                  {empty ?? 'No rows.'}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const id = rowKey(row);
                const isSelected = selection?.selected.has(id) ?? false;
                return (
                  <tr
                    key={id}
                    onClick={() => onRowClick?.(row)}
                    className={`border-b border-divider last:border-b-0 transition cursor-pointer ${
                      isSelected ? 'surface-muted' : 'hover:surface-hover'
                    }`}
                  >
                    {selection && (
                      <td className={`px-3 ${rowPad} align-middle`} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleRow(id)}
                          aria-label="Select row"
                        />
                      </td>
                    )}
                    {columns.map((col) => {
                      const mobileHide = col.mobile === 'hide' ? 'hidden md:table-cell' : '';
                      return (
                        <td
                          key={col.id}
                          className={`px-3 ${rowPad} align-middle min-w-0 ${mobileHide}`}
                          style={col.width ? { width: col.width } : undefined}
                        >
                          {col.render(row, context)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Checkbox({
  checked,
  indeterminate,
  onChange,
  ...rest
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange(): void;
  'aria-label': string;
}) {
  // Indeterminate has to be set imperatively after the DOM input mounts.
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      ref={(el) => {
        if (el) el.indeterminate = Boolean(indeterminate);
      }}
      className="h-4 w-4 rounded border-input cursor-pointer accent-[color:var(--cc-accent)]"
      {...rest}
    />
  );
}

function SortIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M7 14l5-5 5 5" />
    </svg>
  );
}

export function useRowSelection() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  return { selected, setSelected };
}
